import { useCallback, useEffect, useMemo } from 'react'
import { Formik } from 'formik'
import { Box, ChakraProvider, extendTheme } from '@chakra-ui/react'
import * as Yup from 'yup'
import { isNull, isUndefined } from 'lodash'
import MetaDataFields from './MetaDataFields'
import { UserProvider } from '../../../providers'
import {
  fileConstraints,
  formatCommonSettingExtensions,
  formatCustomCoordinates,
  formatCustomMetadata,
  formatExtensions,
  formatTags,
  getCmFieldValidatorMap,
  validateCmField,
  validateCommonSettingCustomCoordinates,
  validateCustomMetadata,
} from './uploadUtils'
import { theme } from '../../../../ui-kit'
import { checkAllowedPostTransformation } from '../../../../utils/mediaLibrary'

const EditMetadata = ({ file, formRef, onSubmit, cmFields, fileVersion }) => {
  const updatedCmField = useMemo(() => {
    return cmFields?.map(data => ({
      ...data,
      name: `IK_TEMP_CUSTOM_METADATA_${data.name}`,
      originalName: data.name,
    }))
  }, [cmFields])

  const cmFieldValidatorMap = getCmFieldValidatorMap(updatedCmField)

  useEffect(() => {
    const insertedElement = document.getElementById('unique') as HTMLElement
    if (insertedElement) {
      insertedElement.style.display = 'none'
    }
  }, [])

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .min(1, 'Minimum length should be 1')
      .required('Mandatory field'),
    customCoordinatesOb: Yup.object()
      .shape({
        ['x-coordinate']: Yup.number().typeError(
          'x-coordinate must be a number',
        ),
        ['y-coordinate']: Yup.number().typeError(
          'y-coordinate must be a number',
        ),
        width: Yup.number().typeError('Width must be a number'),
        height: Yup.number().typeError('Height must be a number'),
      })
      .optional(),
  })

  const initialValues = useMemo(() => {
    if (file) {
      let fileObject = file

      //setting tags
      if (typeof file?.tags === 'string' && file.tags?.length)
        fileObject = formatTags(fileObject, file)
      else if (!isNull(file?.tags)) {
        if (
          file.IK_TEMP_COMMON_SETTINGS?.tags &&
          Array.isArray(file.IK_TEMP_COMMON_SETTINGS?.tags) &&
          file.IK_TEMP_COMMON_SETTINGS?.tags?.length
        )
          fileObject = {
            ...fileObject,
            tags: file.IK_TEMP_COMMON_SETTINGS?.tags,
          }
      }

      // setting custom metadata values
      if (updatedCmField?.length) {
        updatedCmField.forEach(cmData => {
          if (validateCustomMetadata(file, cmData?.originalName))
            fileObject = formatCustomMetadata(
              fileObject,
              file,
              cmData?.name,
              cmData?.originalName,
            )
          else if (
            !(
              file?.customMetadata &&
              typeof file.customMetadata === 'string' &&
              Object.keys(JSON.parse(file.customMetadata)).length &&
              isNull(JSON.parse(file?.customMetadata)[cmData?.originalName])
            )
          ) {
            if (
              validateCustomMetadata(
                file.IK_TEMP_COMMON_SETTINGS,
                cmData?.originalName,
              )
            )
              fileObject = formatCustomMetadata(
                fileObject,
                file.IK_TEMP_COMMON_SETTINGS,
                cmData?.name,
                cmData?.originalName,
              )
            else if (
              validateCustomMetadata(
                file.IK_TEMP_GLOBAL_SETTINGS,
                cmData?.originalName,
              )
            )
              fileObject = formatCustomMetadata(
                fileObject,
                file.IK_TEMP_GLOBAL_SETTINGS,
                cmData?.name,
                cmData?.originalName,
              )
          }
          if (
            !(
              isUndefined(cmData?.schema?.defaultValue) ||
              isNull(cmData?.schema?.defaultValue)
            ) &&
            (isUndefined(fileObject[cmData?.name]) ||
              isNull(fileObject[cmData?.name]))
          ) {
            fileObject = {
              ...fileObject,
              [cmData?.name]: cmData?.schema?.defaultValue,
            }
          }
        })
      }

      if (
        file?.type &&
        fileConstraints.allowedFiles.image.includes(file?.type)
      ) {
        // setting custom coordinates
        if (
          file.customCoordinates &&
          file.customCoordinates?.split(',').length === 4
        )
          fileObject = formatCustomCoordinates(fileObject, file)
        else if (!isNull(file?.customCoordinates)) {
          if (
            validateCommonSettingCustomCoordinates(
              file?.IK_TEMP_COMMON_SETTINGS?.customCoordinatesOb,
            )
          )
            fileObject = {
              ...fileObject,
              customCoordinatesOb:
                file.IK_TEMP_COMMON_SETTINGS?.customCoordinatesOb,
            }
          else if (
            file.IK_TEMP_GLOBAL_SETTINGS?.customCoordinates &&
            file.IK_TEMP_GLOBAL_SETTINGS?.customCoordinates?.split(',')
              .length === 4
          )
            fileObject = formatCustomCoordinates(
              fileObject,
              file?.IK_TEMP_GLOBAL_SETTINGS,
            )
        }

        // setting extensions
        if (file?.extensions?.length)
          fileObject = formatExtensions(
            fileObject,
            JSON.parse(file?.extensions),
          )
        else if (!isNull(file?.extensions)) {
          if (
            file?.IK_TEMP_COMMON_SETTINGS?.extensionsData &&
            (file?.IK_TEMP_COMMON_SETTINGS?.extensionsData?.aiTag ||
              file?.IK_TEMP_COMMON_SETTINGS?.extensionsData?.removeBg ||
              file?.IK_TEMP_COMMON_SETTINGS?.extensionsData?.autoDescription)
          )
            fileObject = formatCommonSettingExtensions(
              fileObject,
              file?.IK_TEMP_COMMON_SETTINGS,
            )
          else if (file?.IK_TEMP_GLOBAL_SETTINGS?.extensions?.length)
            fileObject = formatExtensions(
              fileObject,
              JSON.parse(file?.IK_TEMP_GLOBAL_SETTINGS?.extensions),
            )
        }
      }

      // setting webhook url
      if (!(isUndefined(file?.webhookUrl) || isNull(file?.webhookUrl)))
        fileObject = {
          ...fileObject,
          webhookUrl: file?.webhookUrl,
        }
      else if (!isNull(file?.webhookUrl)) {
        if (
          !(
            isUndefined(file?.IK_TEMP_COMMON_SETTINGS?.webhookUrl) ||
            isNull(file?.IK_TEMP_COMMON_SETTINGS?.webhookUrl)
          )
        )
          fileObject = {
            ...fileObject,
            webhookUrl: file?.IK_TEMP_COMMON_SETTINGS?.webhookUrl,
          }
        else
          fileObject = {
            ...fileObject,
            webhookUrl: '',
          }
      }

      // setting unique name
      if (fileVersion)
        fileObject = {
          ...fileObject,
          useUniqueFileName: false,
        }
      else if (
        !(
          isUndefined(file?.useUniqueFileName) ||
          isNull(file?.useUniqueFileName)
        )
      )
        fileObject = {
          ...fileObject,
          useUniqueFileName: file?.useUniqueFileName,
        }
      else if (
        !(
          isUndefined(file?.IK_TEMP_COMMON_SETTINGS?.useUniqueFileName) ||
          isNull(file?.IK_TEMP_COMMON_SETTINGS?.useUniqueFileName)
        )
      )
        fileObject = {
          ...fileObject,
          useUniqueFileName: file?.IK_TEMP_COMMON_SETTINGS?.useUniqueFileName,
        }
      else if (
        !(
          isUndefined(file?.IK_TEMP_GLOBAL_SETTINGS?.useUniqueFileName) ||
          isNull(file?.IK_TEMP_GLOBAL_SETTINGS?.useUniqueFileName)
        )
      )
        fileObject = {
          ...fileObject,
          useUniqueFileName: file?.IK_TEMP_GLOBAL_SETTINGS?.useUniqueFileName,
        }

      // setting isPrivateFile
      if (!(isUndefined(file?.isPrivateFile) || isNull(file?.isPrivateFile)))
        fileObject = { ...fileObject, isPrivateFile: file?.isPrivateFile }
      else if (
        !(
          isUndefined(file?.IK_TEMP_COMMON_SETTINGS?.isPrivateFile) ||
          isNull(file?.IK_TEMP_COMMON_SETTINGS?.isPrivateFile)
        )
      )
        fileObject = {
          ...fileObject,
          isPrivateFile: file?.IK_TEMP_COMMON_SETTINGS?.isPrivateFile,
        }
      else if (
        !(
          isUndefined(file?.IK_TEMP_GLOBAL_SETTINGS?.isPrivateFile) ||
          isNull(file?.IK_TEMP_GLOBAL_SETTINGS?.isPrivateFile)
        )
      )
        fileObject = {
          ...fileObject,
          isPrivateFile: file?.IK_TEMP_GLOBAL_SETTINGS?.isPrivateFile,
        }

      // setting isPublished
      if (!(isUndefined(file?.isPublished) || isNull(file?.isPublished))) {
        fileObject = { ...fileObject, isPublished: file?.isPublished }
      } else if (
        !(
          isUndefined(file?.IK_TEMP_COMMON_SETTINGS?.isPublished) ||
          isNull(file?.IK_TEMP_COMMON_SETTINGS?.isPublished)
        )
      ) {
        fileObject = {
          ...fileObject,
          isPublished: file?.IK_TEMP_COMMON_SETTINGS?.isPublished,
        }
      } else if (
        !(
          isUndefined(file?.IK_TEMP_GLOBAL_SETTINGS?.isPublished) ||
          isNull(file?.IK_TEMP_GLOBAL_SETTINGS?.isPublished)
        )
      ) {
        fileObject = {
          ...fileObject,
          isPublished: file?.IK_TEMP_GLOBAL_SETTINGS?.isPublished,
        }
      }

      // setting overwriteAITags
      if (
        !(isUndefined(file?.overwriteAITags) || isNull(file?.overwriteAITags))
      )
        fileObject = { ...fileObject, overwriteAITags: file?.overwriteAITags }
      else if (
        !(
          isUndefined(file?.IK_TEMP_COMMON_SETTINGS?.overwriteAITags) ||
          isNull(file?.IK_TEMP_COMMON_SETTINGS?.overwriteAITags)
        )
      )
        fileObject = {
          ...fileObject,
          overwriteAITags: file?.IK_TEMP_COMMON_SETTINGS?.overwriteAITags,
        }
      else if (
        !(
          isUndefined(file?.IK_TEMP_GLOBAL_SETTINGS?.overwriteAITags) ||
          isNull(file?.IK_TEMP_GLOBAL_SETTINGS?.overwriteAITags)
        )
      )
        fileObject = {
          ...fileObject,
          overwriteAITags: file?.IK_TEMP_GLOBAL_SETTINGS?.overwriteAITags,
        }

      // setting overwriteFile
      if (!(isUndefined(file?.overwriteFile) || isNull(file?.overwriteFile)))
        fileObject = { ...fileObject, overwriteFile: file?.overwriteFile }
      else if (
        !(
          isUndefined(file?.IK_TEMP_COMMON_SETTINGS?.overwriteFile) ||
          isNull(file?.IK_TEMP_COMMON_SETTINGS?.overwriteFile)
        )
      )
        fileObject = {
          ...fileObject,
          overwriteFile: file?.IK_TEMP_COMMON_SETTINGS?.overwriteFile,
        }
      else if (
        !(
          isUndefined(file?.IK_TEMP_GLOBAL_SETTINGS?.overwriteFile) ||
          isNull(file?.IK_TEMP_GLOBAL_SETTINGS?.overwriteFile)
        )
      )
        fileObject = {
          ...fileObject,
          overwriteFile: file?.IK_TEMP_GLOBAL_SETTINGS?.overwriteFile,
        }

      // setting overwriteTags
      if (!(isUndefined(file?.overwriteTags) || isNull(file?.overwriteTags)))
        fileObject = { ...fileObject, overwriteTags: file?.overwriteTags }
      else if (
        !(
          isUndefined(file?.IK_TEMP_COMMON_SETTINGS?.overwriteTags) ||
          isNull(file?.IK_TEMP_COMMON_SETTINGS?.overwriteTags)
        )
      )
        fileObject = {
          ...fileObject,
          overwriteTags: file?.IK_TEMP_COMMON_SETTINGS?.overwriteTags,
        }
      else if (
        !(
          isUndefined(file?.IK_TEMP_GLOBAL_SETTINGS?.overwriteTags) ||
          isNull(file?.IK_TEMP_GLOBAL_SETTINGS?.overwriteTags)
        )
      )
        fileObject = {
          ...fileObject,
          overwriteTags: file?.IK_TEMP_GLOBAL_SETTINGS?.overwriteTags,
        }

      // setting overwriteDescription
      if (!(isUndefined(file?.overwriteDescription) || isNull(file?.overwriteDescription)))
        fileObject = { ...fileObject, overwriteDescription: file?.overwriteDescription }
      else if (
        !(
          isUndefined(file?.IK_TEMP_COMMON_SETTINGS?.overwriteDescription) ||
          isNull(file?.IK_TEMP_COMMON_SETTINGS?.overwriteDescription)
        )
      )
        fileObject = {
          ...fileObject,
          overwriteDescription: file?.IK_TEMP_COMMON_SETTINGS?.overwriteDescription,
        }
      else if (
        !(
          isUndefined(file?.IK_TEMP_GLOBAL_SETTINGS?.overwriteDescription) ||
          isNull(file?.IK_TEMP_GLOBAL_SETTINGS?.overwriteDescription)
        )
      )
        fileObject = {
          ...fileObject,
          overwriteDescription: file?.IK_TEMP_GLOBAL_SETTINGS?.overwriteDescription,
        }

      // setting overwriteCustomMetadata
      if (
        !(
          isUndefined(file?.overwriteCustomMetadata) ||
          isNull(file?.overwriteCustomMetadata)
        )
      )
        fileObject = {
          ...fileObject,
          overwriteCustomMetadata: file?.overwriteCustomMetadata,
        }
      else if (
        !(
          isUndefined(file?.IK_TEMP_COMMON_SETTINGS?.overwriteCustomMetadata) ||
          isNull(file?.IK_TEMP_COMMON_SETTINGS?.overwriteCustomMetadata)
        )
      )
        fileObject = {
          ...fileObject,
          overwriteCustomMetadata:
            file?.IK_TEMP_COMMON_SETTINGS?.overwriteCustomMetadata,
        }
      else if (
        !(
          isUndefined(file?.IK_TEMP_GLOBAL_SETTINGS?.overwriteCustomMetadata) ||
          isNull(file?.IK_TEMP_GLOBAL_SETTINGS?.overwriteCustomMetadata)
        )
      )
        fileObject = {
          ...fileObject,
          overwriteCustomMetadata:
            file?.IK_TEMP_GLOBAL_SETTINGS?.overwriteCustomMetadata,
        }
      
        if (
          file.type &&
          (fileConstraints.allowedFiles.image.includes(file.type) ||
            fileConstraints.allowedFiles.video.includes(file.type))
        ) {
          // setting preTransformation
          if (
            !(
              isUndefined(file?.preTransformation) ||
              isNull(file?.preTransformation)
            )
          ) {
            fileObject = {
              ...fileObject,
              preTransformation: file?.preTransformation,
            }
          } else if (!isNull(file?.preTransformation)) {
            if (
              !(
                isUndefined(file?.IK_TEMP_COMMON_SETTINGS?.preTransformation) ||
                isNull(file?.IK_TEMP_COMMON_SETTINGS?.preTransformation) ||
                file?.IK_TEMP_COMMON_SETTINGS?.preTransformation === ''
              )
            ) {
              fileObject = {
                ...fileObject,
                preTransformation:
                  file?.IK_TEMP_COMMON_SETTINGS?.preTransformation,
              }
            } else if (
              !(
                isUndefined(file?.IK_TEMP_GLOBAL_SETTINGS?.preTransformation) ||
                isNull(file?.IK_TEMP_GLOBAL_SETTINGS?.preTransformation)
              )
            ) {
              fileObject = {
                ...fileObject,
                preTransformation:
                  file?.IK_TEMP_GLOBAL_SETTINGS?.preTransformation,
              }
            } else {
              fileObject = {
                ...fileObject,
                preTransformation: '',
              }
            }
          }

          // setting postTransformation
          if (
            !(
              isUndefined(file?.postTransformation) ||
              isNull(file?.postTransformation)
            )
          ) {
            fileObject = {
              ...fileObject,
              postTransformation: file?.postTransformation,
            }
          } else if (!isNull(file?.postTransformation)) {
            if (
              !(
                isUndefined(
                  file?.IK_TEMP_COMMON_SETTINGS?.postTransformation,
                ) || isNull(file?.IK_TEMP_COMMON_SETTINGS?.postTransformation)
              )
            ) {
              fileObject = {
                ...fileObject,
                postTransformation: checkAllowedPostTransformation(
                  file?.IK_TEMP_COMMON_SETTINGS?.postTransformation,
                  file.type,
                ),
              }
            } else if (
              !(
                isUndefined(
                  file?.IK_TEMP_GLOBAL_SETTINGS?.postTransformation,
                ) || isNull(file?.IK_TEMP_GLOBAL_SETTINGS?.postTransformation)
              )
            ) {
              fileObject = {
                ...fileObject,
                postTransformation: checkAllowedPostTransformation(
                  file?.IK_TEMP_GLOBAL_SETTINGS?.postTransformation,
                  file.type,
                ),
              }
            } else {
              fileObject = {
                ...fileObject,
                postTransformation: [],
              }
            }
          }
        }
        // setting customMetadataSelect
        if (!(isUndefined(file?.customMetadataSelect) || isNull(file?.customMetadataSelect))) {
          fileObject = { ...fileObject, customMetadataSelect: file?.customMetadataSelect }
        } else if (
          !(
            isUndefined(file?.IK_TEMP_COMMON_SETTINGS?.customMetadataSelect) ||
            isNull(file?.IK_TEMP_COMMON_SETTINGS?.customMetadataSelect)
          )
        ) {
          fileObject = {
            ...fileObject,
            customMetadataSelect: file?.IK_TEMP_COMMON_SETTINGS?.customMetadataSelect,
          }
        } else {
          fileObject = {
            ...fileObject,
            customMetadataSelect: "cm_default",
          }
        }

        return fileObject
    }
  }, [file, updatedCmField, fileVersion])

  const detectCmFields = useCallback(
    values => {
      const cmFieldsName = updatedCmField.map(data => data.name)
      const customMetadata = Object.keys(values)
        .filter(fieldName => cmFieldsName.includes(fieldName))
        .reduce((ob, data) => ({ ...ob, [data]: values[data] }), {})
      const tempCustomMetadata = {}
      Object.keys(customMetadata).forEach(key => {
        if (
          key.split('IK_TEMP_CUSTOM_METADATA_')?.length &&
          key.split('IK_TEMP_CUSTOM_METADATA_')[1]
        )
          tempCustomMetadata[key.split('IK_TEMP_CUSTOM_METADATA_')[1]] =
            customMetadata[key]
      })
      return JSON.stringify(tempCustomMetadata)
    },
    [updatedCmField],
  )

  return (
    <ChakraProvider theme={extendTheme(theme)}>
      <UserProvider>
        <Box height="100%">
          <Formik
            initialValues={initialValues}
            onSubmit={values =>
              onSubmit({
                ...values,
                customMetadata: detectCmFields(values),
              })
            }
            validationSchema={validationSchema}
            validate={formValues =>
              validateCmField(
                formValues,
                updatedCmField,
                cmFieldValidatorMap,
                false,
              )
            }
            innerRef={formRef}
            enableReinitialize={true}
          >
            {({ handleChange, values, dirty, isValid }) => {


              useEffect(() => {
                const saveChanges = document.querySelector(
                  '.uppy-Dashboard-FileCard-actionsBtn',
                ) as HTMLButtonElement
                saveChanges.disabled = (!isValid || !dirty)
                if (!isValid || !dirty) {
                  saveChanges.disabled = true
                  saveChanges.style.opacity = "0.4"
                  saveChanges.style.boxShadow = "none"
                  saveChanges.style.cursor = "not-allowed"
                  saveChanges.style.backgroundColor = "#ced1d6"
                  saveChanges.style.color = "#3b485e"
                } else {
                  saveChanges.disabled = false
                  saveChanges.style.backgroundColor = "#0450d5"
                  saveChanges.style.opacity = "1"
                  saveChanges.style.color = "white"
                  saveChanges.style.cursor = "pointer"
                }
              }, [dirty, isValid])


              return (
                <MetaDataFields
                  file={file}
                  values={values}
                  handleChange={handleChange}
                  isCommonSettings={false}
                  cmFields={updatedCmField}
                  fileVersion={fileVersion}
                />
              )
            }}
          </Formik>
        </Box>
      </UserProvider>
    </ChakraProvider>
  )
}

export default EditMetadata
